import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../ui/sheet";
import { CartLines } from "./CartLines";
import { AddressForm, EMPTY_ADDRESS } from "./AddressForm";
import { useCart, cartSubtotal } from "../../lib/cart";
import { getShippingQuote, startCheckout, money, errMsg } from "../../lib/shopApi";

const ADDR_KEY = "saltymoo-ship-to";
const SUPPLIER_LABEL = { printful: "Printful package", printed_mint: "Premium goods package" };

const Row = ({ label, value, bold, testid }) => (
  <div className={`flex justify-between text-sm ${bold ? "font-semibold text-[#4B3A2F]" : "text-[#2C2724]"}`}>
    <span>{label}</span><span data-testid={testid}>{value}</span>
  </div>
);

export const CartDrawer = ({ open, onOpenChange, testMode }) => {
  const items = useCart();
  const [address, setAddress] = useState(() => {
    try { return { ...EMPTY_ADDRESS, ...JSON.parse(sessionStorage.getItem(ADDR_KEY)) }; } catch { return EMPTY_ADDRESS; }
  });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setQuote(null); }, [items, address]);
  useEffect(() => { sessionStorage.setItem(ADDR_KEY, JSON.stringify(address)); }, [address]);

  const payload = () => ({
    items: items.map(({ product_id, variant_id, quantity }) => ({ product_id, variant_id, quantity })),
    address,
  });

  const calc = async () => {
    setBusy(true);
    try { setQuote(await getShippingQuote(payload())); }
    catch (e) { toast.error(errMsg(e, "We couldn't calculate shipping.")); }
    finally { setBusy(false); }
  };

  const checkout = async () => {
    setBusy(true);
    try {
      const { checkout_url } = await startCheckout({ ...payload(), origin_url: window.location.origin });
      window.location.assign(checkout_url);
    } catch (e) {
      toast.error(errMsg(e, "We couldn't start checkout."));
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#F9F6F0] border-[#E7DFD5] overflow-y-auto" data-testid="shop-cart-drawer">
        <SheetTitle className="font-display text-3xl text-[#4B3A2F]">Your Basket</SheetTitle>
        <SheetDescription className="font-script text-xl text-[#4B3A2F]">from the Story Barn shelves</SheetDescription>
        {testMode && (
          <p className="mt-3 text-xs rounded bg-[#FFF4D6] border border-[#E9C46A] text-[#6D4C1F] p-2" data-testid="shop-test-mode-banner">
            TEST MODE — no real charges. Use card 4242 4242 4242 4242, any future date, any CVC.
          </p>
        )}
        {!items.length ? (
          <p className="mt-10 text-center font-body text-[#6D5A50]" data-testid="shop-cart-empty">Your basket is empty.</p>
        ) : (
          <>
            <CartLines items={items} />
            <Row label="Items" value={money(cartSubtotal(items))} testid="shop-cart-items-subtotal" />
            <h3 className="font-display text-xl text-[#4B3A2F] mt-8 mb-3">Ship to</h3>
            <AddressForm value={address} onChange={setAddress} />
            {quote && (
              <div className="mt-6 space-y-1.5 border-t border-[#E7DFD5] pt-4" data-testid="shop-quote">
                <Row label="Items" value={money(quote.subtotal)} />
                {quote.shipping_groups.map((g) => (
                  <Row key={g.supplier} label={`Shipping${quote.packages > 1 ? ` · ${SUPPLIER_LABEL[g.supplier]}` : ""}${g.delivery ? ` (${g.delivery})` : ""}`} value={money(g.amount)} testid={`shop-quote-shipping-${g.supplier}`} />
                ))}
                <Row label="Sales tax" value="calculated at checkout" />
                <Row label="Before tax" value={money(quote.subtotal + quote.shipping_total)} bold testid="shop-quote-total" />
                {quote.packages > 1 && <p className="text-xs text-[#6D5A50] pt-1">Your order will arrive in {quote.packages} separate packages.</p>}
              </div>
            )}
            <button type="button" disabled={busy} onClick={quote ? checkout : calc}
              data-testid={quote ? "shop-checkout-button" : "shop-calc-shipping-button"}
              className="mt-6 w-full rounded-full bg-[#D62839] text-white py-3 uppercase tracking-[0.2em] text-xs hover:bg-[#b51f2e] disabled:opacity-60 transition-colors duration-300">
              {busy ? "One moment…" : quote ? "Continue to secure checkout" : "See shipping & total"}
            </button>
            <p className="text-[11px] text-center text-[#6D5A50] mt-3">Payments are handled securely by Stripe.</p>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
